import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import {
  CustomerRestPasswordRequest,
  EmailAvailabilityData,
  ForgetPasswordRequest,
  ForgetPasswordResponse,
  IsEmailAvailableRequest,
  LoginData,
  LoginRequest,
  PetPro,
  RefreshTokenData,
  RefreshTokenRequest,
  SearchPetProRequest,
  SignupData,
  SignupRequest,
} from '@/types/Signup';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

export const authenticationApi = createApi({
  reducerPath: 'authenticationApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `Authentication` }),
  tagTypes: ['Authentication', 'EmailAvailability', 'PetProSearch', 'Signup', 'AdminLogin', 'fluidAppLogin'],
  endpoints: builder => ({
    login: builder.mutation<LoginData, LoginRequest>({
      query: ({ loginName, password, rememberMe, thirdPartyProvider, thirdPartyToken, guestUserSignup }) => {
        // For third-party login, send only thirdPartyProvider, thirdPartyToken, and rememberMe
        if (thirdPartyProvider !== undefined && thirdPartyToken) {
          return {
            url: '/login',
            method: 'POST',
            body: { thirdPartyProvider, thirdPartyToken, rememberMe },
          };
        }
        // For regular login, send userName, loginName, password, and rememberMe
        // Include guestUserSignup flag if provided (for auto-login after enrollment)
        const body: Record<string, any> = { userName: loginName, loginName, password, rememberMe };
        if (guestUserSignup) {
          body.guestUserSignup = true;
        }
        return {
          url: '/login',
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: LoginData, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    refreshToken: builder.mutation<SuccessResponse<RefreshTokenData>, RefreshTokenRequest>({
      query: payload => ({
        url: '/refresh',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<RefreshTokenData>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    isEmailAvailable: builder.mutation<SuccessResponse<EmailAvailabilityData>, IsEmailAvailableRequest>({
      query: payload => ({
        url: '/IsEmailAvailable',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<EmailAvailabilityData>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    searchPetPro: builder.mutation<SuccessResponse<PetPro[]>, SearchPetProRequest>({
      query: payload => ({
        url: '/SearchPetpro',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<PetPro[]>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    signup: builder.mutation<SuccessResponse<SignupData>, SignupRequest>({
      query: payload => ({
        url: '/signup',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<SignupData>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    sendEmailForForgetPassword: builder.mutation<SuccessResponse<ForgetPasswordResponse>, ForgetPasswordRequest>({
      query: payload => ({
        url: '/ForgetPassword',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<ForgetPasswordResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    customerResetPassword: builder.mutation<SuccessResponse<any>, CustomerRestPasswordRequest>({
      query: payload => ({
        url: '/ResetPassword',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<any>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    logout: builder.mutation<SuccessResponse<null>, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<null>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    silentLogin: builder.query<SuccessResponse<{ customerTypeID: number }>, { token: string }>({
      query: ({ token }) => ({
        url: `/AdminLogin?token=${encodeURIComponent(token)}`,
        next: { tags: ['AdminLogin'] },
      }),
      transformResponse: (response: SuccessResponse<{ customerTypeID: number }>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['AdminLogin'],
    }),

    fluidAppLogin: builder.query<SuccessResponse<{ customerTypeID: number }>, { token: string }>({
      query: ({ token }) => ({
        url: `/silentlogin?token=${encodeURIComponent(token)}`,
        next: { tags: ['fluidAppLogin'] },
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<SuccessResponse<{ customerTypeID: number }>>, meta, arg) =>
        response.data,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['fluidAppLogin'],
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useIsEmailAvailableMutation,
  useSearchPetProMutation,
  useSignupMutation,
  useSendEmailForForgetPasswordMutation,
  useCustomerResetPasswordMutation,
  useLogoutMutation,
  useSilentLoginQuery,
  useFluidAppLoginQuery,
} = authenticationApi;
